import React, { useMemo } from 'react';
import { ActivityIcon, ArrowRightIcon, BadgeCheckIcon, Building2Icon, ClipboardCheckIcon, HandCoinsIcon, HeartPulseIcon, HourglassIcon, LandmarkIcon, StethoscopeIcon, UserCheckIcon, UserMinusIcon, UsersIcon, WalletIcon, PlaneIcon, WrenchIcon, FileSpreadsheetIcon } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useHrPayroll } from '../../../contexts/HrPayrollContext';
import { currentPeriod, formatKwacha, formatPeriod, sumKwacha, todayIso } from '../../../utils/hrFormat';
import { HrStatCard } from './HrStatCard';
import type { HrSection } from './HrPayrollModule';
const COLORS = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#0d9488', '#ca8a04', '#4f46e5', '#64748b', '#16a34a', '#dc2626', '#0891b2', '#9333ea'];
const short = (value: number) => value >= 1000 ? `K${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k` : `K${value}`;
export function HrOverview({
  onNavigate
}: {
  onNavigate: (section: HrSection) => void;
}) {
  const {
    employees,
    positions,
    departments,
    leaveRequests,
    loans,
    payrollRuns
  } = useHrPayroll();
  const today = todayIso();
  const stats = useMemo(() => {
    const active = employees.filter(e => e.status === 'Active');
    const onLeaveIds = new Set(leaveRequests.filter(l => l.status === 'Approved' && l.startDate <= today && l.endDate >= today).map(l => l.employeeId));
    const category = (id: string) => positions.find(p => p.id === id)?.category;
    const byCategory = (name: string) => active.filter(e => category(e.positionId) === name).length;
    return {
      total: employees.length,
      active: active.length,
      inactive: employees.length - active.length,
      onLeave: active.filter(e => onLeaveIds.has(e.id)).length,
      doctors: byCategory('Doctors'),
      nurses: byCategory('Nurses'),
      allied: byCategory('Allied Health'),
      admin: byCategory('Administrative'),
      support: byCategory('Support'),
      basic: sumKwacha(active.map(e => e.basicSalary)),
      advances: sumKwacha(loans.filter(l => l.status === 'Active').map(l => l.balance)),
      pending: payrollRuns.filter(r => r.status === 'Draft' || r.status === 'Approved').length,
      completed: payrollRuns.filter(r => r.status === 'Processed' || r.status === 'Paid').length
    };
  }, [employees, positions, leaveRequests, loans, payrollRuns, today]);
  const validRuns = useMemo(() => payrollRuns.filter(r => r.status !== 'Reversed').sort((a, b) => a.period.localeCompare(b.period)), [payrollRuns]);
  const latest = validRuns.find(r => r.period === currentPeriod()) ?? validRuns[validRuns.length - 1];
  const trend = validRuns.slice(-12).map(run => ({
    period: formatPeriod(run.period).replace(/ (\d{2})(\d{2})$/, " '$2"),
    Gross: run.totals.gross,
    Net: run.totals.net,
    'Employer cost': run.totals.employerCost,
    PAYE: run.totals.paye,
    NAPSA: sumKwacha([run.totals.napsaEmployee, run.totals.napsaEmployer]),
    NHIMA: sumKwacha([run.totals.nhimaEmployee, run.totals.nhimaEmployer]),
    SDL: run.totals.sdl
  }));
  const byDepartment = departments.map(dept => {
    const staff = employees.filter(e => e.departmentId === dept.id && e.status === 'Active');
    const salary = latest ? sumKwacha(latest.lines.filter(l => l.departmentId === dept.id).map(l => l.gross)) : sumKwacha(staff.map(e => e.basicSalary));
    return {
      name: dept.name,
      employees: staff.length,
      salary
    };
  }).filter(d => d.employees > 0 || d.salary > 0);
  const status = latest?.status;
  const statusTone = status === 'Paid' ? 'bg-emerald-100 text-emerald-800' : status === 'Processed' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800';
  return <div className="space-y-6" data-id="element-942" data-mp="2uu0svntpsad5">
      <section className="grid gap-4 lg:grid-cols-[1.4fr_1fr]" data-id="element-943" data-mp="2uszjdnscjvxj">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-944" data-mp="10dsnfd9x6xlh">
          <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-945" data-mp="vezd1tpxjmpj">
            <div data-id="element-946" data-mp="vcwcifr5ib6k">
              <p className="text-sm font-medium text-slate-500" data-id="element-947" data-mp="1a3vhzhwowjlx">{latest ? `Payroll — ${formatPeriod(latest.period)}` : 'No payroll processed yet'}</p>
              <p className="mt-1 text-3xl font-bold tabular-nums text-slate-900" data-id="element-948" data-mp="15cdg8rmueezw">{formatKwacha(latest?.totals.net)}</p>
              <p className="text-sm text-slate-500" data-id="element-949" data-mp="1j2qokv3nbvpr">Net pay to {latest?.totals.employees ?? 0} employees</p>
            </div>
            {latest && <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone}`} data-id="element-950" data-mp="1t2hj2ws7iapu">{latest.status}{latest.reference ? ` · ${latest.reference}` : ''}</span>}
          </div>
          <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4" data-id="element-951" data-mp="1o1tusivd47zb">
            {[['Gross payroll', latest?.totals.gross], ['PAYE', latest?.totals.paye], ['NAPSA (ee + er)', latest ? sumKwacha([latest.totals.napsaEmployee, latest.totals.napsaEmployer]) : 0], ['NHIMA (ee + er)', latest ? sumKwacha([latest.totals.nhimaEmployee, latest.totals.nhimaEmployer]) : 0]].map(([label, value]) => <div key={label as string} data-id="element-952" data-mp="1vmw1v1wi7gk8">
                <dt className="text-xs text-slate-500" data-id="element-953" data-mp="20lqqwll2e1cs">{label}</dt>
                <dd className="font-semibold tabular-nums text-slate-900" data-id="element-954" data-mp="20jw5z79sm98p">{formatKwacha(value as number)}</dd>
              </div>)}
          </dl>
          <button type="button" onClick={() => onNavigate('payroll')} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:underline" data-id="element-955" data-mp="1rmlx1skg6w6y">
            Go to Payroll Processing <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-956" data-mp="1hofwbeadnw3o" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4" data-id="element-957" data-mp="373masssbamhj">
          <HrStatCard label="Total employer payroll cost" value={formatKwacha(latest?.totals.employerCost)} icon={LandmarkIcon} tone="violet" data-id="element-958" data-mp="3c1ff1ga216f6" />
          <HrStatCard label="Total monthly basic salaries" value={formatKwacha(stats.basic)} icon={WalletIcon} tone="blue" data-id="element-959" data-mp="3al9xk8yhesj7" />
          <HrStatCard label="Payroll pending approval / pay" value={stats.pending} icon={HourglassIcon} tone="amber" data-id="element-960" data-mp="35judaj44oq1v" />
          <HrStatCard label="Completed payrolls" value={stats.completed} icon={BadgeCheckIcon} tone="emerald" data-id="element-961" data-mp="30nvv5d9e849w" />
          <HrStatCard label="Outstanding salary advances & loans" value={formatKwacha(stats.advances)} icon={HandCoinsIcon} tone="red" emphasis data-id="element-962" data-mp="3taqc40704lim" />
        </div>
      </section>

      <section aria-labelledby="workforce-heading" data-id="element-963" data-mp="3odq7zutppsu5">
        <h2 id="workforce-heading" className="mb-3 text-sm font-semibold text-slate-700" data-id="element-964" data-mp="3jk54xod9i8o3">Workforce</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5" data-id="element-965" data-mp="3fk4pc0effrfg">
          <HrStatCard label="Total employees" value={stats.total} icon={UsersIcon} tone="blue" data-id="element-966" data-mp="fwd9nky2ty97" />
          <HrStatCard label="Active" value={stats.active} icon={UserCheckIcon} tone="emerald" data-id="element-967" data-mp="b5dae91hj43j" />
          <HrStatCard label="Inactive / terminated" value={stats.inactive} icon={UserMinusIcon} tone="slate" data-id="element-968" data-mp="1ts4v63g2tclu" />
          <HrStatCard label="On leave today" value={stats.onLeave} icon={PlaneIcon} tone="amber" data-id="element-969" data-mp="1yqg48byjrj30" />
          <HrStatCard label="Doctors" value={stats.doctors} icon={StethoscopeIcon} tone="violet" data-id="element-970" data-mp="22s1o4h9i0xmp" />
          <HrStatCard label="Nurses" value={stats.nurses} icon={HeartPulseIcon} tone="pink" data-id="element-971" data-mp="27s5wfk096m1u" />
          <HrStatCard label="Allied health" value={stats.allied} icon={ActivityIcon} tone="teal" data-id="element-972" data-mp="2cr90b14hym88" />
          <HrStatCard label="Administrative" value={stats.admin} icon={ClipboardCheckIcon} tone="blue" data-id="element-973" data-mp="2hpailju3mhin" />
          <HrStatCard label="Support staff" value={stats.support} icon={WrenchIcon} tone="slate" data-id="element-974" data-mp="2mg0r35xtn0hk" />
          <HrStatCard label="Departments" value={departments.length} icon={Building2Icon} tone="slate" data-id="element-975" data-mp="2qinnlk5ft9l3" />
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-2" data-id="element-976" data-mp="2vis2j5qp5w97">
        <ChartCard title="Monthly payroll expenditure" empty={trend.length === 0} data-id="element-977" data-mp="30g1y734oj45h">
          <BarChart data={trend} data-id="element-978" data-mp="2bd62t51pe18j">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-979" data-mp="2bc3en4vrmcwb" />
            <XAxis dataKey="period" tick={{
            fontSize: 11
          }} data-id="element-980" data-mp="26el3r8y6tnlo" />
            <YAxis tickFormatter={short} tick={{
            fontSize: 11
          }} width={52} data-id="element-981" data-mp="26g61f7fstmnr" />
            <Tooltip formatter={(v: number) => formatKwacha(v)} data-id="element-982" data-mp="2kdb36v8hbzd7" />
            <Legend wrapperStyle={{
            fontSize: 12
          }} data-id="element-983" data-mp="2gbzkl9o0weei" />
            <Bar dataKey="Gross" fill="#2563eb" radius={[4, 4, 0, 0]} data-id="element-984" data-mp="2gaeohfm28nej" />
            <Bar dataKey="Net" fill="#0d9488" radius={[4, 4, 0, 0]} data-id="element-985" data-mp="1sn6ievgt5abg" />
          </BarChart>
        </ChartCard>
        <ChartCard title="Payroll trend" empty={trend.length === 0} data-id="element-986" data-mp="1npet897js2ay">
          <LineChart data={trend} data-id="element-987" data-mp="21g0chz0wye9d">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-988" data-mp="21h33e6y8kgpa" />
            <XAxis dataKey="period" tick={{
            fontSize: 11
          }} data-id="element-989" data-mp="1wmp6u17u83j1" />
            <YAxis tickFormatter={short} tick={{
            fontSize: 11
          }} width={52} data-id="element-990" data-mp="18xw49lwpmxfg" />
            <Tooltip formatter={(v: number) => formatKwacha(v)} data-id="element-991" data-mp="14x2jv2ih80rb" />
            <Legend wrapperStyle={{
            fontSize: 12
          }} data-id="element-992" data-mp="14wu5152smldh" />
            <Line type="monotone" dataKey="Employer cost" stroke="#7c3aed" strokeWidth={2} dot={false} data-id="element-993" data-mp="usvstteb2krm" />
            <Line type="monotone" dataKey="Net" stroke="#0d9488" strokeWidth={2} dot={false} data-id="element-994" data-mp="zk412m3wg037" />
          </LineChart>
        </ChartCard>
        <ChartCard title="Employees by department" empty={byDepartment.length === 0} data-id="element-995" data-mp="kwst0u6xey7u">
          <PieChart data-id="element-996" data-mp="pukizio0ga9e">
            <Pie data={byDepartment} dataKey="employees" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2} data-id="element-997" data-mp="prye5qsi88uv">
              {byDepartment.map((entry, index) => <Cell key={entry.name} fill={COLORS[index % COLORS.length]} data-id="element-998" data-mp="1ihotu49sbit3" data-mpi={index} />)}
            </Pie>
            <Tooltip data-id="element-999" data-mp="13kfmi14364fq" />
            <Legend wrapperStyle={{
            fontSize: 11
          }} data-id="element-1000" data-mp="13kxoledfjcvk" />
          </PieChart>
        </ChartCard>
        <ChartCard title={latest ? `Salary expenditure by department — ${formatPeriod(latest.period)}` : 'Basic salary by department'} empty={byDepartment.length === 0} data-id="element-1001" data-mp="18iz983qi7k95">
          <BarChart data={byDepartment} layout="vertical" margin={{
          left: 20
        }} data-id="element-1002" data-mp="3tb1dtg37ikpg">
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" data-id="element-1003" data-mp="qjyc3adxp3g4" />
            <XAxis type="number" tickFormatter={short} tick={{
            fontSize: 11
          }} data-id="element-1004" data-mp="virot0lecqf8" />
            <YAxis type="category" dataKey="name" tick={{
            fontSize: 11
          }} width={110} data-id="element-1005" data-mp="vkubpbr963fq" />
            <Tooltip formatter={(v: number) => formatKwacha(v)} data-id="element-1006" data-mp="gtli6rk8bxxb" />
            <Bar dataKey="salary" name="Salary" fill="#2563eb" radius={[0, 4, 4, 0]} data-id="element-1007" data-mp="lrwq2wqyrycw" />
          </BarChart>
        </ChartCard>
        <ChartCard title="Statutory contributions" empty={trend.length === 0} wide data-id="element-1008" data-mp="7rm1k0d4mvuw">
          <BarChart data={trend} data-id="element-1009" data-mp="ct1iphc6oi80">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-1010" data-mp="cpx8a284kq8w" />
            <XAxis dataKey="period" tick={{
            fontSize: 11
          }} data-id="element-1011" data-mp="3u281hoqgy7nt" />
            <YAxis tickFormatter={short} tick={{
            fontSize: 11
          }} width={52} data-id="element-1012" data-mp="2tas3qqqund7" />
            <Tooltip formatter={(v: number) => formatKwacha(v)} data-id="element-1013" data-mp="1s9a73oh1n70r" />
            <Legend wrapperStyle={{
            fontSize: 12
          }} data-id="element-1014" data-mp="1s6xnl6vn0oll" />
            <Bar dataKey="PAYE" stackId="s" fill="#2563eb" data-id="element-1015" data-mp="1x72775rudzg1" />
            <Bar dataKey="NAPSA" stackId="s" fill="#7c3aed" data-id="element-1016" data-mp="3hq5ojay8d6o9" />
            <Bar dataKey="NHIMA" stackId="s" fill="#db2777" data-id="element-1017" data-mp="3hqp4wn88fn06" />
            <Bar dataKey="SDL" stackId="s" fill="#ea580c" radius={[4, 4, 0, 0]} data-id="element-1018" data-mp="3crkvpwlb5p9u" />
          </BarChart>
        </ChartCard>
      </section>
    </div>;
}
function ChartCard({
  title,
  empty,
  wide,
  children
}: {
  title: string;
  empty: boolean;
  wide?: boolean;
  children: React.ReactElement;
}) {
  return <div className={`rounded-xl border border-slate-200 bg-white p-4 shadow-sm ${wide ? 'xl:col-span-2' : ''}`} data-id="element-1019" data-mp="4aq2z9y85l9h">
      <h3 className="mb-3 text-sm font-semibold text-slate-800" data-id="element-1020" data-mp="8mhdshud0ah">{title}</h3>
      {empty ? <div className="flex h-56 flex-col items-center justify-center text-center text-sm text-slate-500" data-id="element-1021" data-mp="e9p2a1x3o4jt">
          <FileSpreadsheetIcon className="mb-2 h-8 w-8 text-slate-300" aria-hidden="true" data-id="element-1022" data-mp="9bdsfr6t1u1l" />
          No payroll data yet. Charts fill in once payroll is calculated.
        </div> : <div className="h-56" data-id="element-1023" data-mp="2b2qywkof89zr">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1024" data-mp="2b3jv4rpr7h3n">{children}</ResponsiveContainer>
        </div>}
    </div>;
}